// Main Application Component
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { User } from './types';
import { OnboardingFlow } from './components/OnboardingFlow';
import { PythonInterviewDashboard } from './components/PythonInterviewDashboard';
import { AssistantModeSelector } from './components/AssistantModeSelector';

import './index.css';

// Session key for localStorage
const SESSION_KEY = 'interview-copilot-session';
const USER_KEY = 'interview-copilot-user';
const AUTH_TOKEN_KEY = 'interview-copilot-auth-token';

// Backend API URL
const BACKEND_API_URL = process.env.REACT_APP_BACKEND_URL || '#';

// Helper to read authenticated profile from localStorage
function getStoredAuthProfile(): { email?: string; fullName?: string } {
  try {
    const authUserData = localStorage.getItem('auth-user-data');
    if (!authUserData) return {};
    const parsed = JSON.parse(authUserData);
    return {
      email: parsed?.email,
      fullName: parsed?.fullName,
    };
  } catch {
    return {};
  }
}

// Helper function to fetch user data from backend
async function fetchUserDataFromBackend(token: string): Promise<{ email: string; fullName?: string; userId: string } | null> {
  try {
    const response = await fetch(`${BACKEND_API_URL}/api/auth/me`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      return {
        email: data.user.email,
        fullName: data.user.full_name || data.user.name,
        userId: data.user.id || data.user._id
      };
    }
  } catch (error) {
    console.error('Failed to fetch user data from backend:', error);
  }
  return null;
}

// Wrapper component to access URL params
function AppContent() {
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [assistantMode, setAssistantMode] = useState<'personalized' | 'general' | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const clearAuthAndSession = () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem('auth-user-data');
    localStorage.removeItem('auth-timestamp');
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    setAssistantMode(null);
    setIsAuthenticated(false);
  };

  useEffect(() => {
    const initializeApp = async () => {
      const handleBeforeUnload = () => {
        localStorage.removeItem(SESSION_KEY);
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      // Check for token in URL or stored token
      const tokenFromUrl = searchParams.get('token');
      const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
      
      let token: string | null = null;
      
      if (tokenFromUrl) {
        // Fresh token from URL (dashboard redirect)
        token = tokenFromUrl;
        console.log('🔑 Fresh token from URL, verifying with backend...');
      } else if (storedToken) {
        token = storedToken;
        console.log('🔑 Using stored token, verifying with backend...');
      }
      
      // If no token found, deny access
      if (!token) {
        console.error('❌ No valid authentication token found. Access denied.');
        setAuthError('Authentication required. Please access this application through the dashboard.');
        clearAuthAndSession();
        setIsLoading(false);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
      }
      
      // Always verify token with backend
      const userData = await fetchUserDataFromBackend(token);
      if (!userData) {
        console.error('❌ Token verification failed. Access denied.');
        setAuthError('Invalid or expired authentication token. Please login again through the dashboard.');
        clearAuthAndSession();
        setIsLoading(false);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
      }
      
      console.log('✅ Authentication successful:', userData);
      setAuthError(null);
      setIsAuthenticated(true);
      
      // Store token and user data with timestamp
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      localStorage.setItem('auth-timestamp', new Date().toISOString());
      
      const userWithAuth = {
        email: userData.email,
        fullName: userData.fullName,
        userId: userData.userId
      };
      localStorage.setItem('auth-user-data', JSON.stringify(userWithAuth));
      
      const sessionExists = localStorage.getItem(SESSION_KEY);
      const savedUser = localStorage.getItem(USER_KEY);
      
      if (!sessionExists) {
        localStorage.removeItem(USER_KEY);
        setUser(null);
        setIsLoading(false);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
      }
      
      if (savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          
          // Merge with auth user data
          const authUserData = localStorage.getItem('auth-user-data');
          if (authUserData) {
            const authData = JSON.parse(authUserData);
            userData.email = authData.email;
            userData.fullName = authData.fullName;
          }
          
          setUser(userData);
          localStorage.setItem(SESSION_KEY, new Date().toISOString());
        } catch (error) {
          console.error('Error loading saved user data:', error);
          localStorage.removeItem(USER_KEY);
          localStorage.removeItem(SESSION_KEY);
          setUser(null);
        }
      }
      
      setIsLoading(false);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    };
    
    initializeApp();
  }, [searchParams]);

  // Save user data to localStorage when it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      // Ensure session is still active when user data changes
      localStorage.setItem(SESSION_KEY, new Date().toISOString());
    }
  }, [user]);

  const handleOnboardingComplete = (userData: Partial<User>) => {
    const authProfile = getStoredAuthProfile();
    const newUser: User = {
      id: userData.id!, // Use the ID from onboarding (already set)
      jobTitle: userData.jobTitle!,
      resumeData: userData.resumeData!,
      jobDescription: userData.jobDescription!,
      systemPrompt: userData.systemPrompt!,
      createdAt: new Date(),
      email: authProfile.email,
      fullName: authProfile.fullName
    };
    
    console.log(`🔍 App.tsx: Using user ID: ${newUser.id}`);
    
    // Create new session
    localStorage.setItem(SESSION_KEY, new Date().toISOString());
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    
    setUser(newUser);
  };

  const handleLeaveSession = async () => {
    // Return to mode selector without logging out
    console.log('🔄 Leaving session - returning to mode selector');

    // Safety net: freeze billing if a session row is still active.
    // Stop is idempotent (200 even if already stopped / never started).
    if (user?.sessionId) {
      try {
        const { stopInterviewCopilotBillingSession } = await import('./services/saasBillingService');
        await stopInterviewCopilotBillingSession(user.sessionId);
        console.log('✅ Billing stop on leave acknowledged');
      } catch (error) {
        console.warn('⚠️ Billing stop on leave failed:', error);
      }
    }
    
    // Clear backend session data
    if (user?.id) {
      try {
        const { PythonBackendService } = await import('./services/pythonBackendService');
        const backendService = new PythonBackendService();
        await backendService.clearUserSession(user.id);
        console.log('✅ Backend session data cleared');
      } catch (error) {
        console.error('⚠️ Failed to clear backend session data:', error);
        // Continue anyway - don't block the user from leaving
      }
    }
    
    setUser(null);
    setAssistantMode(null);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(SESSION_KEY);
    // Keep authentication artifacts on "Leave Session".
    console.log('✅ Interview session cleared; authentication preserved');
  };

  const handleModeSelect = async (mode: 'personalized' | 'general') => {
    // Re-validate auth when user selects a mode tile.
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      setAuthError('Authentication required. Please access this application through the dashboard.');
      clearAuthAndSession();
      return;
    }

    const verifiedUser = await fetchUserDataFromBackend(token);
    if (!verifiedUser) {
      setAuthError('Your session is invalid or expired. Please login again through the dashboard.');
      clearAuthAndSession();
      return;
    }

    // Keep stored auth profile fresh after successful tile click verification.
    localStorage.setItem('auth-user-data', JSON.stringify({
      email: verifiedUser.email,
      fullName: verifiedUser.fullName,
      userId: verifiedUser.userId
    }));
    localStorage.setItem('auth-timestamp', new Date().toISOString());

    setAssistantMode(mode);
    
    // If general mode, create a minimal user object and go straight to dashboard
    if (mode === 'general') {
      const authProfile = getStoredAuthProfile();
      const userId = `general_user_${Date.now()}`;
      const generalUser: User = {
        id: userId,
        jobTitle: 'General Interview Practice',
        resumeData: {
          personalInfo: {
            name: '',
            email: '',
            phone: '',
            location: ''
          },
          experience: [],
          education: [],
          skills: [],
          certifications: [],
          projects: []
        },
        jobDescription: 'General interview preparation and practice',
        systemPrompt: 'You are a helpful AI interview assistant providing general interview guidance and support.',
        createdAt: new Date(),
        email: authProfile.email,
        fullName: authProfile.fullName
      };
      
      // Send minimal preferences to backend to establish user context
      try {
        const { PythonBackendService } = await import('./services/pythonBackendService');
        const backendService = new PythonBackendService();
        
        // Generate unified session ID for general mode user
        const sessionId = `session_${Date.now()}`;
        console.log(`🆔 Generated session ID for general mode: ${sessionId}`);
        generalUser.sessionId = sessionId;
        
        console.log('📤 Sending minimal preferences for general mode user...');
        await backendService.setUserPreferences({
          userId: userId,
          jobTitle: generalUser.jobTitle,
          jobDescription: generalUser.jobDescription,
          systemPrompt: generalUser.systemPrompt,
          resumeFile: '', // Empty resume for general mode
          resumeFileType: '',
          manualTriggerEnabled: false, // Default to automatic mode
          sessionId: sessionId // ✅ Add unified session ID
        });
        console.log('✅ General mode user context established in backend');
      } catch (error) {
        console.error('❌ Failed to set up general mode user context:', error);
        alert('Failed to initialize general mode. Please try again.');
        setAssistantMode(null);
        return;
      }
      
      // Save to localStorage
      localStorage.setItem(SESSION_KEY, new Date().toISOString());
      localStorage.setItem(USER_KEY, JSON.stringify(generalUser));
      
      setUser(generalUser);
    }
    // For personalized mode, user will go through onboarding flow
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-primary-700 font-medium">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Show unauthorized access screen if authentication failed
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Access Denied</h1>
          <p className="text-gray-600 mb-6">
            {authError || 'You need to be authenticated to access Interview CoPilot.'}
          </p>
          <div className="space-y-3">
            <a
              href={process.env.REACT_APP_DASHBOARD_URL || '#'}
              className="block w-full px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
            >
              Go to Dashboard
            </a>
            <p className="text-sm text-gray-500">
              Please access Interview CoPilot through your dashboard
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Routes>
        <Route 
          path="/" 
          element={
            user ? (
              <Navigate to="/dashboard" replace />
            ) : !assistantMode ? (
              <AssistantModeSelector
                onSelectMode={handleModeSelect}
              />
            ) : assistantMode === 'personalized' ? (
              <OnboardingFlow
                onComplete={handleOnboardingComplete}
                onCancel={handleLeaveSession}
              />
            ) : (
              // assistantMode is 'general' but user is null (shouldn't happen, but fallback to mode selector)
              <AssistantModeSelector
                onSelectMode={handleModeSelect}
              />
            )
          } 
        />
        
        <Route 
          path="/dashboard" 
          element={
            user ? (
              <PythonInterviewDashboard
                user={user}
                onLeaveSession={handleLeaveSession}
                onSessionIdChange={(sessionId) => {
                  setUser((prev) => {
                    if (!prev) return prev;
                    const next = { ...prev, sessionId };
                    localStorage.setItem(USER_KEY, JSON.stringify(next));
                    return next;
                  });
                }}
              />
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
        

        

        
        
        {/* Catch all route - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
